import React, {useRef, useEffect} from 'react';
import {motion, useAnimation} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import {Link} from 'react-router-dom';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {FaArrowRight} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import createServiceData from '../../../services/data/serviceData';
import './ServicesCarousel.css';

const ServicesCarousel: React.FC = () => {
    const {t} = useTranslation();
    const servicesData = Object.values(createServiceData(t));

    const controls = useAnimation();
    const [ref, inView] = useInView({
        threshold: 0.2,
    });

    useEffect(() => {
        if (inView) {
            controls.start('visible');
        }
    }, [controls, inView]);

    const sliderRef = useRef<Slider | null>(null);

    const settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        pauseOnHover: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1
                }
            },
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1
                }
            }
        ]
    };

    const containerVariants = {
        hidden: {opacity: 0},
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: {y: 20, opacity: 0},
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5
            }
        }
    };

    return (
        <section className="services-section">
            <div className="container">
                <motion.div
                    className="section-header"
                    initial={{opacity: 0, y: 30}}
                    whileInView={{opacity: 1, y: 0}}
                    transition={{duration: 0.6}}
                    viewport={{once: true}}
                >
                    <h2 className="section-title">
                        {t('home.services.title', 'Nos domaines d’intervention')}
                    </h2>
                    <p className="section-subtitle">
                        {t('home.services.subtitle', 'Explorez l’éventail complet de nos services techniques et industriels, adaptés à vos besoins spécifiques. Expertise, réactivité et professionnalisme sont au cœur de nos interventions.')}
                    </p>
                </motion.div>

                <motion.div
                    className="carousel-container"
                    ref={ref}
                    variants={containerVariants}
                    initial="hidden"
                    animate={controls}
                >
                    <Slider ref={sliderRef} {...settings} className="styled-slider">
                        {servicesData.map((service) => (
                            <motion.div key={service.id} variants={itemVariants}>
                                <div className="service-card">
                                    <div className="service-image-container">
                                        <img
                                            className="service-image"
                                            src={service.image}
                                            alt={service.title}
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = '/images/services/placeholder-service.webp';
                                            }}
                                        />
                                        <div className="service-overlay">
                                            <div className="service-icon">
                                                {service.icon}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="service-content">
                                        <h3 className="service-title">{service.title}</h3>
                                        <p className="service-description">
                                            {service.subtitle || service.description.substring(0, 120).replace(/<\/?[^>]+(>|$)/g, "")}...
                                        </p>
                                        <Link to={`/services/${service.slug}`} className="service-link"
                                              onClick={() => window.scrollTo(0, 0)}
                                        >
                                            {t('common.learnMore', 'En savoir plus')}
                                            <FaArrowRight/>
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </Slider>
                </motion.div>

                <motion.div
                    className="button-container"
                    initial={{opacity: 0}}
                    whileInView={{opacity: 1}}
                    transition={{delay: 0.8, duration: 0.6}}
                    viewport={{once: true}}
                >
                    <Link to="/services" className="view-all-button btn btn-primary"
                          onClick={() => window.scrollTo(0, 0)}>
                        {t('home.services.seeMore', 'Consulter l’ensemble de nos services')}
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default ServicesCarousel;
